import React, { useState, useEffect } from 'react'
import { Language } from './data/types'
import { categories } from './data'
import DocsNavigation from './DocsNavigation'
import EndpointCard from './EndpointCard'
import CodeExample from './CodeExample'
import FlowDiagram from './FlowDiagram'
import ArchitectureDiagram from './ArchitectureDiagram'
import WebviewDocs from './WebviewDocs'
import { DocType } from './DocTypeSelector'
import './ApiDocs.css'

interface ApiDocsProps {
    initialDocType?: DocType
}

const ApiDocs: React.FC<ApiDocsProps> = ({ initialDocType = 'api' }) => {
    const [language, setLanguage] = useState<Language>('pt')
    const [docType, setDocType] = useState<DocType>(initialDocType)
    const [activeSection, setActiveSection] = useState<string>(initialDocType === 'api' ? 'introduction' : 'webview-introduction')

    // Reset active section when changing doc type
    useEffect(() => {
        if (docType === 'api') {
            setActiveSection('introduction')
        } else {
            setActiveSection('webview-introduction')
        }
        window.scrollTo({ top: 0 })
    }, [docType])

    useEffect(() => {
        const handleScroll = () => {
            const sections = document.querySelectorAll('[id]')
            let currentSection = 'introduction'

            sections.forEach((section) => {
                const rect = section.getBoundingClientRect()
                if (rect.top <= 100 && rect.bottom >= 100) {
                    currentSection = section.id
                }
            })

            setActiveSection(currentSection)
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const handleSectionClick = (sectionId: string) => {
        const element = document.getElementById(sectionId)
        if (element) {
            const offset = 80
            const elementPosition = element.getBoundingClientRect().top + window.scrollY
            window.scrollTo({
                top: elementPosition - offset,
                behavior: 'smooth'
            })
        }
    }

    return (
        <div className="api-docs-container">
            <div className="api-docs-wrapper">
                <DocsNavigation
                    currentLanguage={language}
                    onLanguageChange={setLanguage}
                    activeSection={activeSection}
                    onSectionClick={handleSectionClick}
                    docType={docType}
                    onDocTypeChange={setDocType}
                />

                {docType === 'webview' ? (
                    <WebviewDocs language={language} />
                ) : (
                    <div className="docs-main-content">
                {/* Introduction */}
                <section id="introduction">
                    <div className="docs-header">
                        <h1 className="docs-title">
                            {language === 'pt' ? 'Documentação da API EmpreX' : 'EmpreX API Documentation'}
                        </h1>
                        <p className="docs-description">
                            {language === 'pt'
                                ? 'Bem-vindo à documentação da API EmpreX. Esta API fornece serviços financeiros para usuários finais, incluindo gerenciamento de empréstimos, processamento de pagamentos e integrações.'
                                : 'Welcome to the EmpreX API documentation. This API provides financial services for end users, including loan management, payment processing, and integrations.'
                            }
                        </p>
                    </div>

                    {/* Overview Section */}
                    <div className="auth-section">
                        <h2 className="docs-category-title">
                            {language === 'pt' ? 'Visão Geral' : 'Overview'}
                        </h2>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'A API EmpreX conecta parceiros (administradores) e seus usuários finais (clientes) através de um fluxo estruturado de empréstimos e pagamentos. A arquitetura é dividida em duas camadas principais:'
                                    : 'The EmpreX API connects partners (administrators) and their end users (clients) through a structured loan and payment flow. The architecture is divided into two main layers:'
                                }
                            </p>

                            <h3 style={{ fontSize: '18px', fontWeight: 600, marginTop: '20px', marginBottom: '12px' }}>
                                {language === 'pt' ? '👥 Atores do Sistema' : '👥 System Actors'}
                            </h3>

                            <div style={{ marginLeft: '20px', marginBottom: '20px' }}>
                                <p style={{ marginBottom: '12px' }}>
                                    <strong style={{ color: '#10b981' }}>
                                        {language === 'pt' ? '🏢 Admin (Parceiro EmpreX)' : '🏢 Admin (EmpreX Partner)'}
                                    </strong>
                                </p>
                                <ul style={{ marginLeft: '20px' }}>
                                    <li>{language === 'pt' ? 'Empresas parceiras que oferecem empréstimos aos seus clientes para reduzir churn e gerar nova fonte de receita' : 'Partner companies that offer loans to their clients to reduce churn and generate new revenue streams'}</li>
                                    <li>{language === 'pt' ? 'Gerenciam seus clientes/usuários e solicitam tokens em nome deles' : 'Manage their clients/users and request tokens on their behalf'}</li>
                                    <li>{language === 'pt' ? 'Processam cobranças, geram faturas e acessam relatórios administrativos' : 'Process collections, generate invoices, and access administrative reports'}</li>
                                    <li>{language === 'pt' ? 'Autenticação via Bearer Token ou x-api-key (longa duração)' : 'Authentication via Bearer Token or x-api-key (long-lived)'}</li>
                                </ul>

                                <p style={{ marginTop: '16px', marginBottom: '12px' }}>
                                    <strong style={{ color: '#2e7d32' }}>
                                        {language === 'pt' ? '👤 Client (Usuário Final)' : '👤 Client (End User)'}
                                    </strong>
                                </p>
                                <ul style={{ marginLeft: '20px' }}>
                                    <li>{language === 'pt' ? 'Usuários finais da plataforma do parceiro' : 'End users of the partner platform'}</li>
                                    <li>{language === 'pt' ? 'Solicitam ofertas de empréstimo, assinam contratos e realizam pagamentos' : 'Request loan offers, sign contracts, and make payments'}</li>
                                    <li>{language === 'pt' ? 'Consultam histórico, documentos (CCB, recibos) e validam chaves PIX' : 'Check history, documents (CCB, receipts), and validate PIX keys'}</li>
                                    <li>{language === 'pt' ? 'Autenticação via JWT Token (expira em 1 hora, gerado pelo Admin)' : 'Authentication via JWT Token (expires in 1 hour, generated by Admin)'}</li>
                                </ul>
                            </div>

                            <h3 style={{ fontSize: '18px', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
                                {language === 'pt' ? '🔄 Fluxo de Empréstimo' : '🔄 Loan Flow'}
                            </h3>

                            <FlowDiagram
                                language={language}
                                steps={[
                                    {
                                        icon: '📱',
                                        title: { pt: 'Admin gera token', en: 'Admin generates token' },
                                        endpoint: 'POST /admin/get-token',
                                        description: {
                                            pt: 'Parceiro solicita JWT para o usuário final acessar a API',
                                            en: 'Partner requests JWT for end user to access API'
                                        },
                                        color: '#10b981'
                                    },
                                    {
                                        icon: '💰',
                                        title: { pt: 'Client solicita oferta', en: 'Client requests offer' },
                                        endpoint: 'POST /client/loan-offer',
                                        description: {
                                            pt: 'Usuário consulta condições (juros, parcelas) para valor desejado',
                                            en: 'User checks terms (interest, installments) for desired amount'
                                        },
                                        color: '#2e7d32'
                                    },
                                    {
                                        icon: '📝',
                                        title: { pt: 'Client envia dados pessoais', en: 'Client submits personal data' },
                                        endpoint: 'POST /client/personal-data',
                                        description: {
                                            pt: 'Cadastro de CPF, endereço, telefone, etc.',
                                            en: 'Registration of CPF, address, phone, etc.'
                                        },
                                        color: '#2e7d32'
                                    },
                                    {
                                        icon: '🔑',
                                        title: { pt: 'Client valida PIX', en: 'Client validates PIX' },
                                        endpoint: 'POST /client/query-pix',
                                        description: {
                                            pt: 'Validação da chave PIX para desembolso',
                                            en: 'PIX key validation for disbursement'
                                        },
                                        color: '#2e7d32'
                                    },
                                    {
                                        icon: '🔐',
                                        title: { pt: 'Client obtém transaction ID', en: 'Client gets transaction ID' },
                                        endpoint: 'POST /client/get-transaction-id',
                                        description: {
                                            pt: 'ID do módulo de KYC para validação biométrica',
                                            en: 'KYC module ID for biometric validation'
                                        },
                                        color: '#2e7d32'
                                    },
                                    {
                                        icon: '🖊️',
                                        title: { pt: 'Client assina empréstimo', en: 'Client signs loan' },
                                        endpoint: 'POST /client/sign-loan',
                                        description: {
                                            pt: 'Aceite dos termos e assinatura digital',
                                            en: 'Terms acceptance and digital signature'
                                        },
                                        color: '#2e7d32'
                                    },
                                    {
                                        icon: '📄',
                                        title: { pt: 'Client obtém CCB', en: 'Client gets CCB' },
                                        endpoint: 'POST /client/contract-document',
                                        description: {
                                            pt: 'Download do contrato de empréstimo (Cédula de Crédito Bancário)',
                                            en: 'Download loan contract (Banking Credit Note)'
                                        },
                                        color: '#2e7d32'
                                    },
                                    {
                                        icon: '💸',
                                        title: { pt: 'Sistema desembolsa', en: 'System disburses' },
                                        description: {
                                            pt: 'Valor creditado na conta do usuário via PIX',
                                            en: 'Amount credited to user account via PIX'
                                        },
                                        color: '#f57c00'
                                    }
                                ]}
                            />

                            <h3 style={{ fontSize: '18px', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
                                {language === 'pt' ? '💳 Fluxo de Pagamento' : '💳 Payment Flow'}
                            </h3>

                            <FlowDiagram
                                language={language}
                                steps={[
                                    {
                                        icon: '🧾',
                                        title: { pt: 'Client gera fatura', en: 'Client generates invoice' },
                                        endpoint: 'POST /client/create-invoice',
                                        description: {
                                            pt: 'Gera boleto ou código para pagamento via módulo de pagamento',
                                            en: 'Generates payment slip or code via payment module'
                                        },
                                        color: '#2e7d32'
                                    },
                                    {
                                        icon: '💰',
                                        title: { pt: 'Client realiza pagamento', en: 'Client makes payment' },
                                        description: {
                                            pt: 'Pagamento processado via módulo de pagamento (Boleto/PIX/Cartão)',
                                            en: 'Payment processed via payment module (Boleto/PIX/Card)'
                                        },
                                        color: '#6a737d'
                                    },
                                    {
                                        icon: '✅',
                                        title: { pt: 'Webhook de confirmação', en: 'Confirmation webhook' },
                                        description: {
                                            pt: 'Módulo de pagamento notifica API sobre pagamento confirmado',
                                            en: 'Payment module notifies API about confirmed payment'
                                        },
                                        color: '#f57c00'
                                    },
                                    {
                                        icon: '📊',
                                        title: { pt: 'Admin processa cobrança', en: 'Admin processes collection' },
                                        endpoint: 'POST /admin/collection',
                                        description: {
                                            pt: 'Parceiro registra pagamento manualmente (se necessário)',
                                            en: 'Partner records payment manually (if needed)'
                                        },
                                        color: '#10b981'
                                    },
                                    {
                                        icon: '🧾',
                                        title: { pt: 'Client obtém recibo', en: 'Client gets receipt' },
                                        endpoint: 'GET /client/receipt',
                                        description: {
                                            pt: 'Download do comprovante de pagamento',
                                            en: 'Download payment receipt'
                                        },
                                        color: '#2e7d32'
                                    }
                                ]}
                            />

                            <h3 style={{ fontSize: '18px', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
                                {language === 'pt' ? '🏗️ Arquitetura da API' : '🏗️ API Architecture'}
                            </h3>

                            <ArchitectureDiagram language={language} />
                        </div>
                    </div>

                    {/* Getting Started */}
                    <div className="auth-section">
                        <h2 className="docs-category-title">
                            {language === 'pt' ? 'Primeiros Passos' : 'Getting Started'}
                        </h2>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'A API EmpreX é organizada em torno de REST. Nossa API possui URLs orientadas a recursos, retorna respostas codificadas em JSON e usa códigos de resposta HTTP padrão.'
                                    : 'The EmpreX API is organized around REST. Our API has resource-oriented URLs, returns JSON-encoded responses, and uses standard HTTP response codes.'
                                }
                            </p>
                            <ul>
                                <li><strong>Base URL:</strong> <code>https://api.emprex.com.br</code></li>
                                <li><strong>{language === 'pt' ? 'Formato de resposta' : 'Response format'}:</strong> JSON</li>
                                <li><strong>{language === 'pt' ? 'Codificação' : 'Encoding'}:</strong> UTF-8</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Authentication */}
                <section id="authentication" className="docs-category">
                    <div className="docs-category-header">
                        <h2 className="docs-category-title">
                            {language === 'pt' ? 'Autenticação' : 'Authentication'}
                        </h2>
                        <p className="docs-category-description">
                            {language === 'pt'
                                ? 'A API EmpreX utiliza dois métodos de autenticação dependendo do tipo de operação.'
                                : 'The EmpreX API uses two authentication methods depending on the operation type.'
                            }
                        </p>
                    </div>

                    <div className="auth-section">
                        <div className="auth-method">
                            <h3 className="auth-method-title">
                                {language === 'pt' ? 'Autenticação Administrativa' : 'Admin Authentication'}
                            </h3>
                            <div className="auth-method-content">
                                <p>
                                    {language === 'pt'
                                        ? 'Endpoints administrativos são usados pelo parceiro para gerenciar a integração com a EmpreX. Utilizam Bearer tokens ou API keys de longa duração.'
                                        : 'Admin endpoints are used by the partner to manage integration with EmpreX. Use long-lived Bearer tokens or API keys.'
                                    }
                                </p>
                                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderLeft: '4px solid #10b981', borderRadius: '4px' }}>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#059669' }}>
                                        <strong>{language === 'pt' ? '📌 Responsabilidade:' : '📌 Responsibility:'}</strong> {language === 'pt' ? 'Gerar tokens JWT para usuários finais, processar cobranças (retenção percentual ou parcelamento no billing), e acessar relatórios administrativos.' : 'Generate JWT tokens for end users, process payments (percentage retention or billing installments), and access administrative reports.'}
                                    </p>
                                </div>
                                <ul>
                                    <li><strong>Header:</strong> <code>Authorization: Bearer &lt;BEARER_TOKEN&gt;</code> {language === 'pt' ? 'ou' : 'or'} <code>x-api-key: &lt;API_KEY&gt;</code></li>
                                    <li><strong>{language === 'pt' ? 'Tipo de Token' : 'Token Type'}:</strong> {language === 'pt' ? 'API key de longa duração ou Bearer token' : 'Long-lived API key or Bearer token'}</li>
                                    <li><strong>{language === 'pt' ? 'Geração' : 'Generation'}:</strong> {language === 'pt' ? 'Configurado em variáveis de ambiente' : 'Configured in environment variables'}</li>
                                    <li><strong>{language === 'pt' ? 'Uso' : 'Usage'}:</strong> {language === 'pt' ? 'Obrigatório para Webview e API REST completa' : 'Required for both Webview and Full REST API'}</li>
                                </ul>
                                <CodeExample
                                    code={`curl -X POST https://api.emprex.com.br/api/admin/collection \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"loanId": "loan-123", "amount": 275}'`}
                                    language="bash"
                                />
                            </div>
                        </div>

                        <div className="auth-method">
                            <h3 className="auth-method-title">
                                {language === 'pt' ? 'Autenticação de Cliente' : 'Client Authentication'}
                            </h3>
                            <div className="auth-method-content">
                                <p>
                                    {language === 'pt'
                                        ? 'Endpoints de cliente são usados pelos usuários finais para realizar operações de empréstimo. Utilizam tokens JWT (JSON Web Token) de curta duração para autenticação.'
                                        : 'Client endpoints are used by end users to perform loan operations. Use short-lived JWT (JSON Web Token) tokens for authentication.'
                                    }
                                </p>
                                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff4e6', borderLeft: '4px solid #f59e0b', borderRadius: '4px' }}>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#d97706' }}>
                                        <strong>{language === 'pt' ? '📌 Responsabilidade:' : '📌 Responsibility:'}</strong> {language === 'pt' ? 'Solicitar ofertas de empréstimo, enviar dados pessoais, validar PIX, assinar contratos e gerar faturas de pagamento. Necessária apenas se você construir seu próprio front-end (não é necessária se usar o Webview da EmpreX).' : 'Request loan offers, submit personal data, validate PIX, sign contracts and generate payment invoices. Only required if you build your own front-end (not needed if using EmpreX Webview).'}
                                    </p>
                                </div>
                                <ul>
                                    <li><strong>Header:</strong> <code>Authorization: Bearer &lt;JWT_TOKEN&gt;</code></li>
                                    <li><strong>{language === 'pt' ? 'Tipo de Token' : 'Token Type'}:</strong> JWT ({language === 'pt' ? 'expira em 1 hora' : 'expires in 1 hour'})</li>
                                    <li><strong>{language === 'pt' ? 'Geração' : 'Generation'}:</strong> {language === 'pt' ? 'Via' : 'Via'} <code>/api/admin/get-token</code></li>
                                    <li><strong>{language === 'pt' ? 'Uso' : 'Usage'}:</strong> {language === 'pt' ? 'Apenas para API REST completa (não necessário para Webview)' : 'Only for Full REST API (not needed for Webview)'}</li>
                                </ul>
                                <CodeExample
                                    code={`curl -X GET https://api.emprex.com.br/api/client/contract \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."`}
                                    language="bash"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Admin API */}
                {categories
                    .filter((cat) => cat.id === 'admin-api')
                    .map((category) => (
                        <section key={category.id} id={category.id} className="docs-category">
                            <div className="docs-category-header">
                                <h2 className="docs-category-title">{category.title[language]}</h2>
                                <p className="docs-category-description">{category.description[language]}</p>
                            </div>
                            {category.endpoints.map((endpoint) => (
                                <EndpointCard key={endpoint.id} endpoint={endpoint} language={language} />
                            ))}
                        </section>
                    ))}

                {/* Client API */}
                {categories
                    .filter((cat) => cat.id === 'client-api')
                    .map((category) => (
                        <section key={category.id} id={category.id} className="docs-category">
                            <div className="docs-category-header">
                                <h2 className="docs-category-title">{category.title[language]}</h2>
                                <p className="docs-category-description">{category.description[language]}</p>
                            </div>
                            {category.endpoints.map((endpoint) => (
                                <EndpointCard key={endpoint.id} endpoint={endpoint} language={language} />
                            ))}
                        </section>
                    ))}
                </div>
                )}
            </div>
        </div>
    )
}

export default ApiDocs
